import { useMemo } from 'react';
import { SparkParams } from './types';

// Default parameters
export const defaultParams: SparkParams = {
  // Global controls
  globalSpread: 1.0,                // Default global spread
  globalVerticalSpread: 1.0,        // Default vertical spread
  globalHorizontalSpread: 1.0,      // Default horizontal spread
  globalVerticalStretch: 1.0,       // Default vertical stretch
  globalHorizontalStretch: 1.0,     // Default horizontal stretch
  globalDensity: 1.0,               // Default density
  minParticleSize: 3.0,             // Default min particle size
  maxParticleSize: 6.0,             // Default max particle size
  
  // Default state controls
  defaultSpread: 1.0,               // Default spread
  defaultVerticalSpread: 1.0,       // Default vertical spread
  defaultHorizontalSpread: 1.0,     // Default horizontal spread
  defaultVerticalStretch: 1.0,      // Default vertical stretch
  defaultHorizontalStretch: 1.0,    // Default horizontal stretch
  defaultDensity: 1.0,              // Default density
  
  // Hover state controls
  hoverSpread: 1.0,                 // Hover spread
  hoverVerticalSpread: 1.0,         // Hover vertical spread
  hoverHorizontalSpread: 1.0,       // Hover horizontal spread
  hoverVerticalStretch: 1.0,        // Hover vertical stretch
  hoverHorizontalStretch: 1.0,      // Hover horizontal stretch
  hoverDensity: 1.0                 // Hover density
};

// Button-specific parameters
export const buttonParams: SparkParams = {
  // Global controls
  globalSpread: 0.7,                // Custom button global spread
  globalVerticalSpread: 0.9,        // Custom button vertical spread
  globalHorizontalSpread: 1.1,      // Custom button horizontal spread
  globalVerticalStretch: 1.0,       // Default vertical stretch
  globalHorizontalStretch: 1.4,     // Custom button horizontal stretch
  globalDensity: 1.0,               // Default density
  minParticleSize: 0.8,             // Custom button min particle size
  maxParticleSize: 1.7,             // Custom button max particle size
  
  // Default state controls
  defaultSpread: 0.9,               // Custom button default spread
  defaultVerticalSpread: 0.8,       // Custom button default vertical spread
  defaultHorizontalSpread: 0.8,     // Custom button default horizontal spread
  defaultVerticalStretch: 1.0,      // Default vertical stretch
  defaultHorizontalStretch: 1.0,    // Default horizontal stretch
  defaultDensity: 1.4,              // Custom button default density
  
  // Hover state controls
  hoverSpread: 0.4,                 // Custom button hover spread
  hoverVerticalSpread: 0.7,         // Custom button hover vertical spread
  hoverHorizontalSpread: 1.2,       // Custom button hover horizontal spread
  hoverVerticalStretch: 0.8,        // Custom button hover vertical stretch
  hoverHorizontalStretch: 1.5,      // Custom button hover horizontal stretch
  hoverDensity: 1.3                 // Custom button hover density
};

// Link-specific parameters
export const linkParams: SparkParams = {
  // Global controls
  globalSpread: 1.2,                // Custom link global spread
  globalVerticalSpread: 1.0,        // Default vertical spread
  globalHorizontalSpread: 1.0,      // Default horizontal spread
  globalVerticalStretch: 1.1,       // Custom link vertical stretch
  globalHorizontalStretch: 1.2,     // Custom link horizontal stretch
  globalDensity: 1.0,               // Default density
  minParticleSize: 1.0,             // Custom link min particle size
  maxParticleSize: 8.0,             // Custom link max particle size
  
  // Default state controls
  defaultSpread: 1.0,               // Default spread
  defaultVerticalSpread: 1.0,       // Default vertical spread
  defaultHorizontalSpread: 1.0,     // Default horizontal spread
  defaultVerticalStretch: 1.6,      // Custom link default vertical stretch
  defaultHorizontalStretch: 1.0,    // Default horizontal stretch
  defaultDensity: 1.0,              // Default density
  
  // Hover state controls
  hoverSpread: 1.0,                 // Default hover spread
  hoverVerticalSpread: 6.9,         // Custom link hover vertical spread (high value)
  hoverHorizontalSpread: 0.9,       // Custom link hover horizontal spread
  hoverVerticalStretch: 1.3,        // Custom link hover vertical stretch
  hoverHorizontalStretch: 2.3,      // Custom link hover horizontal stretch
  hoverDensity: 1.0                 // Default hover density
};

// Color palettes
export const defaultColors = [
  '251,248,204', '253,228,207', '255,207,210',
  '241,192,232', '207,186,240', '163,196,243',
  '144,219,244', '142,236,245', '152,245,225',
  '185,251,192'
];

export const hoverColors = [...defaultColors];

// Local storage key for custom parameters
const STORAGE_KEY = 'spark-debug-params';

// Try to load custom parameters from localStorage
const loadCustomParams = (): Record<string, SparkParams> | null => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved) : null;
  } catch (e) {
    console.error('Error loading spark parameters:', e);
    return null;
  }
};

// Get the custom parameters or fall back to defaults
const getCustomParams = (): Record<string, SparkParams> => {
  const customParams = loadCustomParams();
  if (!customParams) {
    return {
      default: defaultParams,
      button: buttonParams,
      link: linkParams
    };
  }
  return customParams;
};

// Memoized function to get parameters based on element type
export const getParamsForElement = (
  element: Element, 
  overrideParams?: Record<string, SparkParams>
): SparkParams => {
  // Use provided override params if available, otherwise check for saved params
  const customParams = overrideParams || getCustomParams();
  
  // Get the spark type from the element
  const sparkType = element.getAttribute('data-spark-type');
  
  switch(sparkType) {
    case 'button':
      return customParams.button || buttonParams;
    case 'link':
      return customParams.link || linkParams;
    case 'default':
    default:
      return customParams.default || defaultParams;
  }
};

// Custom hook that could be used in components that need 
// to access spark parameters
export const useSparkParams = (elementType: 'button' | 'link' | 'default' = 'default') => {
  return useMemo(() => {
    const customParams = getCustomParams();
    
    switch(elementType) {
      case 'button':
        return customParams.button || buttonParams;
      case 'link':
        return customParams.link || linkParams;
      case 'default':
      default:
        return customParams.default || defaultParams;
    }
  }, [elementType]);
};
